import { Box, Button, useToast } from "@chakra-ui/react";
import { ApiMessageStack, Message, TableV2 } from "components";
import {
  DEFAULT_ERROR_TOAST_OPTIONS,
  DEFAULT_SUCCESS_TOAST_OPTIONS,
} from "constants/default-toast-options";
import { AlertStatusEnum } from "enums/alert-status.enum";
import { IntegrationEntitySyncStatusEnum } from "enums/integration-entity-sync-status.enum";
import { keyBy } from "lodash";
import { observer } from "mobx-react";
import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { AccountIntegrationAutomatedReferralsStore } from "store/UserAccounts/UserAccount/AccountIntegrations/AccountIntegrationAutomatedReferrals.store";
import { TAutomatedReferralSyncOfficeExtendedData } from "types/automated-referrals.type";
import { IntegrationConfiguratorLayout } from "../../IntegrationConfiguratorLayout";
import { SyncStatusFilter } from "../../SyncStatusFilter";
import { syncAutomatedReferralOfficeTableColumnDef } from "./syncAutomatedReferralOfficeTableColumnDef";

interface IProps {
  automatedReferralsStore: AccountIntegrationAutomatedReferralsStore;
}

const DEFAULT_PAGE_SIZE = 10;

export const AutomatedReferralsOfficeConfigurator: FC<IProps> = observer(
  ({ automatedReferralsStore }) => {
    const toast = useToast();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [newRelations, setNewRelations] = useState<
      Record<number, TAutomatedReferralSyncOfficeExtendedData>
    >({});

    const {
      automatedReferralsOfficeRelationData,
      filteredAutomatedReferralsOfficeRelationData,
      getSyncStatusFilter,
      setSyncStatusFilter,
    } = automatedReferralsStore;

    useEffect(() => {
      const relations = keyBy<TAutomatedReferralSyncOfficeExtendedData>(
        automatedReferralsOfficeRelationData,
        syncData => syncData.officeId
      );

      setNewRelations({
        ...relations,
      });
    }, [automatedReferralsOfficeRelationData, setNewRelations]);

    const updateSyncPair = useCallback(
      (syncPair: TAutomatedReferralSyncOfficeExtendedData) => {
        setNewRelations({
          ...newRelations,
          [syncPair.officeId]: syncPair,
        });
      },
      [setNewRelations, newRelations]
    );

    const submitSyncPairs = useCallback(async () => {
      try {
        setIsSubmitting(true);
        await automatedReferralsStore.updateAccountAutomatedReferralsOfficeRelations(
          Object.values(newRelations)
        );
        toast({
          ...DEFAULT_SUCCESS_TOAST_OPTIONS,
          description: (
            <ApiMessageStack
              messageStack={"automated referral office relation updated"}
            />
          ),
        });
      } catch (error) {
        toast({
          ...DEFAULT_ERROR_TOAST_OPTIONS,
          description: <ApiMessageStack messageStack={error.message} />,
        });
      } finally {
        setIsSubmitting(false);
      }
    }, [automatedReferralsStore, newRelations, toast]);

    const columnsDef = useMemo(() => {
      return syncAutomatedReferralOfficeTableColumnDef(updateSyncPair);
    }, [updateSyncPair]);

    const dataSource = useMemo(() => {
      return filteredAutomatedReferralsOfficeRelationData.map(syncData => {
        if (!!newRelations[syncData.officeId])
          return newRelations[syncData.officeId];

        return syncData;
      });
    }, [filteredAutomatedReferralsOfficeRelationData, newRelations]);

    const banner = useMemo(() => {
      const notSyncedArray = automatedReferralsOfficeRelationData.filter(
        syncData =>
          syncData.syncStatus !== IntegrationEntitySyncStatusEnum.SYNCED
      );

      if (notSyncedArray.length > 0) {
        return (
          <Message status={AlertStatusEnum.WARNING}>
            <Box>
              {`${notSyncedArray.length} of your offices are not configured.`}
            </Box>
          </Message>
        );
      }

      return null;
    }, [automatedReferralsOfficeRelationData]);

    const handleSyncFilterChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setSyncStatusFilter(e.target.checked);
      },
      [setSyncStatusFilter]
    );

    return (
      <IntegrationConfiguratorLayout>
        <Box fontSize={"xl"}>Office mappings</Box>
        {banner}
        <TableV2<TAutomatedReferralSyncOfficeExtendedData>
          globalFilterInputPlaceholder={"search"}
          dataSource={dataSource}
          additionalActions={
            <SyncStatusFilter
              isChecked={getSyncStatusFilter}
              onChange={handleSyncFilterChange}
            />
          }
          columns={columnsDef}
          pageSize={DEFAULT_PAGE_SIZE}
        />
        <Box width={"100%"} textAlign={"right"}>
          <Button
            colorScheme={"blue"}
            onClick={submitSyncPairs}
            isLoading={isSubmitting}
          >
            Update
          </Button>
        </Box>
      </IntegrationConfiguratorLayout>
    );
  }
);
